import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useContext,
  useEffect,
} from "react";
import { CountdownTimerContext } from "components";

type Props = {
  liveMinute: number;
  endMinute: number;
  value: number;
  onChange: (v: number) => void;
  labels?: { left?: string; mid?: string; right?: string };
};

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

const MinuteSlider: React.FC<Props> = ({
  liveMinute,
  endMinute,
  value,
  onChange,
  labels,
}) => {
  const max = Math.max(1, Math.floor(endMinute));

  const { minuteFloat } = useContext(CountdownTimerContext) || ({} as any);

  const liveFromCtx =
    typeof minuteFloat === "number" && Number.isFinite(minuteFloat)
      ? minuteFloat
      : liveMinute;

  const live = clamp(liveFromCtx, 0, max);

  const floorLive = Math.floor(live);

  const val = clamp(Math.floor(value), 0, floorLive);

  const ref = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState(false);

  const [followLive, setFollowLive] = useState(() => {
    return Math.floor(value) >= floorLive;
  });

  const selectedFloat = followLive ? live : val;
  const selectedVal = followLive ? Math.floor(live) : val;

  const pctLive = useMemo(() => (live / max) * 100, [live, max]);
  const pctSelected = useMemo(
    () => (selectedFloat / max) * 100,
    [selectedFloat, max]
  );

  const leftFilledPct = useMemo(() => Math.min(pctSelected, 50), [pctSelected]);
  const rightFilledPct = useMemo(
    () => (pctSelected > 50 ? Math.min(pctSelected - 50, 50) : 0),
    [pctSelected]
  );

  const handlePosToValue = useCallback(
    (clientX: number) => {
      const host = ref.current;
      if (!host) return val;
      const r = host.getBoundingClientRect();
      const x = clamp(clientX - r.left, 0, r.width);
      const ratio = x / r.width;
      const raw = Math.round(ratio * max);
      return clamp(raw, 0, floorLive);
    },
    [max, floorLive, val]
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      (e.target as Element).setPointerCapture?.(e.pointerId);
      setFollowLive(false);
      setDragging(true);
      onChange(handlePosToValue(e.clientX));
    },
    [onChange, handlePosToValue]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging) return;
      onChange(handlePosToValue(e.clientX));
    },
    [dragging, onChange, handlePosToValue]
  );

  const onPointerUp = useCallback(() => {
    setDragging(false);
    if (Math.floor(value) === floorLive) {
      setFollowLive(true);
    }
  }, [value, floorLive]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        setFollowLive(false);
        onChange(clamp(val - 1, 0, floorLive));
      } else if (e.key === "ArrowRight") {
        setFollowLive(false);
        onChange(clamp(val + 1, 0, floorLive));
      } else if (e.key === "Home") {
        setFollowLive(false);
        onChange(0);
      } else if (e.key === "End") {
        onChange(floorLive);
        setFollowLive(true);
      }
    },
    [val, floorLive, onChange]
  );

  useEffect(() => {
    if (!followLive) return;
    if (Math.floor(value) !== floorLive) {
      onChange(floorLive);
    }
  }, [followLive, floorLive, value, onChange]);

  const { numericMinutes, numericSeconds } =
    useContext(CountdownTimerContext) || ({} as any);
  const bubbleMinutes =
    followLive && typeof numericMinutes === "number"
      ? numericMinutes
      : selectedVal;
  const bubbleSeconds =
    followLive && typeof numericSeconds === "number" ? numericSeconds : 0;
  const bubbleMinutesText = String(bubbleMinutes).padStart(2, "0");
  const bubbleSecondsText = String(bubbleSeconds).padStart(2, "0");

  const leftLabel = labels?.left ?? "0:00";
  const midLabel = labels?.mid ?? "FH";
  const rightLabel = labels?.right ?? `${Math.floor(endMinute)}:00`;

  return (
    <div className="w-full select-none px-[2vw] py-4">
      <div className="relative">
        <div
          ref={ref}
          role="slider"
          tabIndex={0}
          aria-valuemin={0}
          aria-valuemax={floorLive}
          aria-valuenow={selectedVal}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onKeyDown={onKeyDown}
          className="relative w-full h-2.5 rounded-full bg-lightGray"
        >
          {leftFilledPct > 0 && (
            <div
              className="absolute top-0 h-full rounded-full bg-yellow"
              style={{
                left: "0%",
                width: `${leftFilledPct}%`,
              }}
            />
          )}
          {rightFilledPct > 0 && (
            <div
              className="absolute top-0 h-full rounded-full bg-yellow"
              style={{
                left: "50%",
                width: `${rightFilledPct}%`,
              }}
            />
          )}

          {!followLive && (
            <div
              aria-hidden
              className="absolute -top-1 -bottom-1 w-0.5 bg-red"
              style={{
                left: `${pctLive}%`,
              }}
            />
          )}

          <div
            className="absolute top-1/2 h-4 w-4 -translate-y-1/2 -translate-x-1/2 rounded-full bg-yellow shadow-[0_2px_0_rgba(0,0,0,0.45),0_4px_10px_rgba(0,0,0,0.35)]"
            style={{
              left: `${pctSelected}%`,
            }}
          />

          <div
            className="absolute bottom-full mb-2 -translate-x-1/2 text-xs font-medium text-yellow"
            style={{
              left: `${pctSelected}%`,
            }}
          >
            {bubbleMinutesText}:{bubbleSecondsText}
          </div>

          <div className="absolute left-1/2 top-full mt-2 -translate-x-1/2 text-sm font-bold text-white">
            {midLabel}
          </div>
        </div>

        <div className="mt-2 flex w-full justify-between text-sm font-bold text-white">
          <div>{leftLabel}</div>
          <div className="invisible">spacer</div>
          <div>{rightLabel}</div>
        </div>
      </div>
    </div>
  );
};

export default MinuteSlider;
